import i18n from "i18next";
import {initReactI18next} from "react-i18next";

const resources = {
    en: {
        translation: {
            common: {
                yes: "Yes",
                no: "No",
                cancel: "Cancel",
                saveChanges: "Save Changes",
                refresh: "Refresh",
            },
            errors: {
                INVALID_REQUEST: "Invalid request",
                NOT_FOUND: "Entity not found",
                CONFLICT: "Request could not be completed",
                USER_NAME_CONFLICT: "A user with this name already exists",
                LEGAL_ENTITY_NAME_CONFLICT: "A legal entity with this name already exists",
                CHAT_DOCUMENTS_UNAVAILABLE: "Chat documents must be uploaded and available before sending",
                FILE_UPLOAD_FAILED: "Failed to upload file",
                TECHNICAL_ERROR: "A technical error has occurred",
                UNAUTHORIZED: "Unauthorized",
                WRONG_OLD_PASSWORD: "The current password is incorrect",
                FORBIDDEN: "Access denied",
                UNPROCESSABLE_CONTENT:
                    "The submitted prompt was blocked because it violates content guidelines or contains unsafe instructions.",
            },
            banner: {
                title: "Purchase Contract Manager",
                logoAlt: "Chatbot logo",
            },
            accessDenied: {
                eyebrow: "Restricted Area",
                title: "Access denied",
                description: "You do not have permission to view this page.",
                backToHome: "Back to home",
                goToLogin: "Go to login",
            },
            admin: {
                openDashboard: "Open admin dashboard",
                shared: {
                    backToChat: "Back to chat",
                    notSet: "Not set",
                    legalEntity: "Legal Entity",
                    user: "User",
                    allUsers: "All users",
                    llmModel: "LLM Model",
                    model: "Model",
                    language: "Language",
                    createdAt: "Created At",
                    from: "From",
                    to: "To",
                    actions: "Actions",
                    name: "Name",
                    password: "Password",
                    month: "Month",
                    year: "Year",
                    unknown: "Unknown",
                },
                dashboard: {
                    title: "Admin dashboard",
                    managedUsers: "Managed Users",
                    messagesSent: "Messages Sent",
                    activeLlm: "Active LLM",
                    tabs: {
                        legalEntityManagement: "Legal Entity Management",
                        userManagement: "User Management",
                        llmConfig: "LLM Config",
                        usage: "Usage",
                        auditing: "Auditing",
                        billing: "Billing",
                    },
                },
                auditing: {
                    eyebrow: "Cost Visibility",
                    title: "Per-message auditing",
                    refreshSnapshot: "Refresh snapshot",
                    emptyState: "Select a legal entity to enable auditing results.",
                    filters: {
                        is: "Is",
                        after: "After",
                        onOrAfter: "On or After",
                        before: "Before",
                        onOrBefore: "On or Before",
                        between: "Between",
                    },
                    columns: {
                        inputTokensUsed: "Input Tokens Used",
                        outputTokensUsed: "Output Tokens Used",
                        response: "Response",
                        latestMessage: "Latest Message",
                        systemPrompt: "System Prompt",
                    },
                },
                llmConfig: {
                    eyebrow: "Configure LLM Model",
                    title: "Global default and legal entity overrides",
                    defaultSection: {
                        eyebrow: "Default",
                        title: "Configure global default model",
                        description: "Used automatically for legal entities without an explicit override.",
                        defaultLlmModel: "Default LLM Model",
                        refreshModels: "Refresh Models",
                        saveDefault: "Save Default",
                    },
                    overridesSection: {
                        eyebrow: "Overrides",
                        title: "Add legal entity override",
                        description:
                            "Create exceptions only where a legal entity should not inherit the global default.",
                        addOverride: "Add Override",
                        selectDifferentModel:
                            "Select a model different from the global default before adding an override.",
                        allLegalEntitiesConfigured: "Every legal entity already has an override configured.",
                    },
                    currentOverrides: {
                        eyebrow: "Legal Entity Overrides",
                        title: "Current overrides",
                        description: "Override rows take precedence over the global default until they are removed.",
                        empty: "No legal entity overrides configured. All legal entities currently inherit the global default.",
                    },
                    columns: {
                        overrideModel: "Override Model",
                    },
                    tooltips: {
                        adjustOverride: "Adjust override",
                        removeOverride: "Remove override",
                    },
                    editDialog: {
                        title: "Adjust legal entity override",
                        matchesDefault:
                            "This matches the global default. Remove the override instead if the legal entity should inherit it.",
                    },
                    removeDialog: {
                        title: "Remove legal entity override",
                        message:
                            "Remove the override for {{legalEntityName}}? It will fall back to the global default model.",
                        confirm: "Remove Override",
                        fallbackLegalEntity: "this legal entity",
                    },
                },
                usage: {
                    eyebrow: "Billing Window",
                    title: "Usage overview",
                    dailyMetric: "Daily Metric",
                    loadingBillingCycle: "Loading billing cycle",
                    selectLegalEntityToLoad: "Select a legal entity to load usage",
                    selectedLegalEntityFallback: "the selected legal entity",
                    selectionSummary:
                        "Viewing {{month}} {{year}} for {{legalEntityName}}. The cycle resets on the 1st of each month, and cost is still mocked here.",
                    dailyUsage: "Daily usage",
                    previousWeek: "Previous week",
                    previousMonth: "Previous month",
                    nextWeek: "Next week",
                    nextMonth: "Next month",
                    metricOnDate: "{{value}} on {{date}}",
                    loadingUsage: "Loading billing-cycle usage...",
                    metrics: {
                        cost: "Cost",
                        totalTokens: "Total Tokens",
                        inputTokens: "Input Tokens",
                        outputTokens: "Output Tokens",
                        messagesSent: "Messages Sent",
                    },
                    emptyState: {
                        title: "Choose a legal entity to view usage.",
                        description:
                            "Billing summaries and daily activity appear after a legal entity is selected. Daily cost is still mocked in this tab.",
                    },
                    summary: {
                        costThisCycle: "Cost This Cycle",
                        billingCycleStarted: "Billing cycle started {{date}}",
                        aggregatedAcrossAllUsers: "Aggregated across all users",
                        filteredToSelectedUser: "Filtered to the selected user",
                        totalTokens: "Total Tokens",
                        allTokenUsage: "All token usage this cycle",
                        selectedUserTokenUsage: "Token usage for the selected user",
                    },
                },
                legalEntityManagement: {
                    eyebrow: "Workspace Structure",
                    title: "Legal entities",
                    description: "Create and review legal entities used across the admin workspace.",
                    actions: {
                        createLegalEntity: "Create Legal Entity",
                    },
                    columns: {
                        billingAddress: "Billing Address",
                        postalAddress: "Postal Address",
                        taxId: "Tax ID",
                    },
                    createDialog: {
                        title: "Create new legal entity",
                        optional: "Optional",
                    },
                },
                userManagement: {
                    eyebrow: "Workspace Access",
                    title: "Users",
                    selectLegalEntityAlert: "Select a legal entity to unlock the user table for that workspace.",
                    emptyState: "Select a legal entity to enable user management results.",
                    actions: {
                        createUser: "Create User",
                    },
                    columns: {
                        role: "Role",
                        preferredLocale: "Preferred Locale",
                    },
                    roles: {
                        admin: "Admin",
                        user: "User",
                    },
                    softDelete: {
                        tooltip: "Soft delete user",
                        ariaLabel: "Soft delete {{name}}",
                    },
                    createUserDialog: {
                        title: "Create new user",
                        selectLegalEntity: "Select legal entity",
                    },
                },
                billing: {
                    eyebrow: "Billing Management",
                    title: "Billing Overview",
                    defaultPrice: {
                        eyebrow: "Default Price",
                        title: "Configure global default price",
                        description: "Used automatically when a legal entity does not have its own billing override.",
                        label: "Default price per token",
                        helperText: "Enter the baseline price that applies to all legal entities without an override.",
                        save: "Save Default Price",
                    },
                    legalEntityPrice: {
                        eyebrow: "Legal Entity Price",
                        title: "Configure legal entity pricing",
                        description: "Set a legal entity-specific price or remove the override to fall back to the default.",
                        label: "Legal entity price per token",
                        helperText: "Enter the price per token for this legal entity.",
                        overrideHelper: "This legal entity currently uses its own billing price override.",
                        inheritsDefault: "This legal entity currently inherits the global default price.",
                        selectLegalEntity: "Select a legal entity to edit or delete its pricing override.",
                        save: "Save Legal Entity Price",
                        delete: "Delete Override",
                        deleteDialog: {
                            title: "Delete legal entity pricing",
                            message:
                                "Delete the pricing override for {{legalEntityName}}? It will fall back to the global default price.",
                            confirm: "Delete Override",
                            fallbackLegalEntity: "this legal entity",
                        },
                    },
                    timeGranularity: "Time Granularity",
                    chartTitle: "Billing activity",
                    chartRange: "{{startDate}} - {{endDate}}",
                    loading: "Loading billing data...",
                    rangeIncomplete: "Choose both a start and end date to load billing data.",
                    invalidRange: "The start date must be on or before the end date.",
                    selectedLegalEntityFallback: "the selected legal entity",
                    selectionSummary:
                        "Viewing {{granularity}} billing between {{startDate}} and {{endDate}} for {{legalEntityName}}.",
                    metricOnDate: "{{value}} on {{date}}",
                    metrics: {
                        tokens: "Arena-Token",
                        price: "Price",
                    },
                    granularity: {
                        daily: "Daily",
                        weekly: "Weekly",
                        monthly: "Monthly",
                        yearly: "Yearly",
                    },
                    emptyState: {
                        title: "Choose a legal entity to view billing.",
                        description:
                            "Billing buckets appear after a legal entity is selected and a date range is available.",
                    },
                    noData: {
                        title: "No billing data in this range.",
                        description: "Try a different date range or a broader time granularity.",
                    },
                    exportCsv: {
                        label: "Download CSV",
                        loading: "Downloading CSV...",
                    },
                    applyFilters: "Apply",
                }
            },

            settings: {
                title: "Settings",
                language: "Language",
                theme: "Theme",
                light: "Light",
                dark: "Dark",
                en: "English",
                de: "German",
                changePassword: "Change password",
                logout: "Logout",
                logoutConfirmation: "Are you sure you want to log out?",
                openMenu: "Open settings",
                changePasswordDialog: {
                    title: "Change password",
                    currentPassword: "Current password",
                    newPassword: "New password",
                    confirmPassword: "Confirm new password",
                    submit: "Change password",
                    passwordsDoNotMatch: "The new passwords do not match.",
                },
            },
            chat: {
                newChat: "New Chat",
                copyToClipboard: "Copy to clipboard",
                edit: "Edit message",
                editPlaceholder: "Edit your message",
                saveEdit: "Save edit",
                cancelEdit: "Cancel edit",
                loading: "Loading...",
                inputPlaceholder: 'Ask your question about the book "Contract Law in Procurement"',
                send: "Send",
                noChatSelected: "Select an existing chat or start a new one to begin.",
                attachments: {
                    openMenu: "Open attachment menu",
                    uploadFile: "Upload file",
                    dropPrompt: "Drop PDF, TXT, DOC, DOCX, MD or PPTX files here",
                    helperText: "PDF, TXT, DOC, DOCX, MD or PPTX only, up to {{maxSizeMb}} MB each, maximum {{maxFiles}} files.",
                    status: {
                        uploading: "File upload in progress.",
                        unavailable: "Remove expired or missing files before sending.",
                    },
                    tooltips: {
                        uploading: "Uploading file",
                        expired: "This file expired. Remove it and upload it again.",
                        missing: "This file is no longer available. Remove it and upload it again.",
                    },
                    errors: {
                        tooManyFiles: "You can upload a maximum of {{maxFiles}} files.",
                        fileTooLarge: "{{fileName}} exceeds the {{maxSizeMb}} MB limit.",
                        unsupportedType: "{{fileName}} is not a supported file type. Upload a PDF, TXT, DOC, DOCX, MD or PPTX file only.",
                        duplicateFile: "{{fileName}} is already attached and was ignored.",
                        duplicateFiles: "{{count}} duplicate files were ignored.",
                    },
                },
            },
            chatHistory: {
                noHistory: "No history found",
                moreActions: "More actions",
                rename: "Rename",
                delete: "Delete",
            },
            login: {
                signInTitle: "Sign in",
                usernameLabel: "Username",
                passwordLabel: "Password",
                signIn: "Sign In",
                signingIn: "Signing in...",
                fillAllFields: "Please fill in all fields.",
                loginFailed: "Login failed. Please try again.",
            },
        },
    },
    de: {
        translation: {
            common: {
                yes: "Ja",
                no: "Nein",
                cancel: "Abbrechen",
                saveChanges: "Änderungen speichern",
            },
            errors: {
                INVALID_REQUEST: "Ungültige Anfrage",
                NOT_FOUND: "Entität nicht gefunden",
                CONFLICT: "Die Anfrage konnte nicht abgeschlossen werden",
                USER_NAME_CONFLICT: "Ein Nutzer mit diesem Namen existiert bereits",
                LEGAL_ENTITY_NAME_CONFLICT: "Eine Rechtseinheit mit diesem Namen existiert bereits",
                CHAT_DOCUMENTS_UNAVAILABLE:
                    "Chat-Dokumente müssen hochgeladen und verfügbar sein, bevor eine Nachricht gesendet wird",
                FILE_UPLOAD_FAILED: "Datei konnte nicht hochgeladen werden",
                TECHNICAL_ERROR: "Ein technischer Fehler ist aufgetreten",
                UNAUTHORIZED: "Nicht angemeldet",
                WRONG_OLD_PASSWORD: "Das aktuelle Passwort ist falsch",
                FORBIDDEN: "Zugriff verweigert",
                UNPROCESSABLE_CONTENT:
                    "Die übermittelte Eingabe wurde blockiert, da sie gegen die Inhaltsrichtlinien verstößt oder unsichere Anweisungen enthält.",
            },
            banner: {
                title: "Einkaufsmanager",
                logoAlt: "Chatbot-Logo",
            },
            accessDenied: {
                eyebrow: "Geschützter Bereich",
                title: "Zugriff verweigert",
                description: "Sie haben keine Berechtigung, diese Seite aufzurufen.",
                backToHome: "Zur Startseite",
                goToLogin: "Zum Login",
            },
            admin: {
                openDashboard: "Admin-Dashboard öffnen",
                shared: {
                    backToChat: "Zurück zum Chat",
                    notSet: "Nicht festgelegt",
                    legalEntity: "Rechtseinheit",
                    user: "Nutzer",
                    allUsers: "Alle Nutzer",
                    llmModel: "LLM-Modell",
                    model: "Modell",
                    language: "Sprache",
                    createdAt: "Erstellt am",
                    from: "Von",
                    to: "Bis",
                    actions: "Aktionen",
                    name: "Name",
                    password: "Passwort",
                    month: "Monat",
                    year: "Jahr",
                    unknown: "Unbekannt",
                },
                dashboard: {
                    title: "Admin-Dashboard",
                    managedUsers: "Verwaltete Nutzer",
                    messagesSent: "Gesendete Nachrichten",
                    activeLlm: "Aktives LLM",
                    tabs: {
                        legalEntityManagement: "Rechtseinheiten",
                        userManagement: "Nutzerverwaltung",
                        llmConfig: "LLM-Konfiguration",
                        usage: "Nutzung",
                        auditing: "Prüfung",
                        billing: "Rechnungsstellung"
                    },
                },
                auditing: {
                    eyebrow: "Kostentransparenz",
                    title: "Prüfung pro Nachricht",
                    refreshSnapshot: "Momentaufnahme aktualisieren",
                    emptyState: "Wählen Sie eine Rechtseinheit aus, um Prüfergebnisse anzuzeigen.",
                    filters: {
                        is: "Ist",
                        after: "Nach",
                        onOrAfter: "Am oder nach",
                        before: "Vor",
                        onOrBefore: "Am oder vor",
                        between: "Zwischen",
                    },
                    columns: {
                        inputTokensUsed: "Verwendete Eingabetokens",
                        outputTokensUsed: "Verwendete Ausgabetokens",
                        response: "Antwort",
                        latestMessage: "Letzte Nachricht",
                        systemPrompt: "System-Prompt",
                    },
                },
                llmConfig: {
                    eyebrow: "LLM-Modell konfigurieren",
                    title: "Globaler Standard und Überschreibungen pro Rechtseinheit",
                    defaultSection: {
                        eyebrow: "Standard",
                        title: "Globales Standardmodell konfigurieren",
                        description: "Wird automatisch für Rechtseinheiten ohne explizite Überschreibung verwendet.",
                        defaultLlmModel: "Standard-LLM-Modell",
                        saveDefault: "Standard speichern",
                    },
                    overridesSection: {
                        eyebrow: "Überschreibungen",
                        title: "Überschreibung für Rechtseinheit hinzufügen",
                        description:
                            "Erstellen Sie Ausnahmen nur dort, wo eine Rechtseinheit nicht den globalen Standard erben soll.",
                        addOverride: "Überschreibung hinzufügen",
                        selectDifferentModel:
                            "Wählen Sie vor dem Hinzufügen einer Überschreibung ein anderes Modell als den globalen Standard aus.",
                        allLegalEntitiesConfigured:
                            "Für jede Rechtseinheit ist bereits eine Überschreibung konfiguriert.",
                    },
                    currentOverrides: {
                        eyebrow: "Überschreibungen pro Rechtseinheit",
                        title: "Aktuelle Überschreibungen",
                        description:
                            "Überschreibungen haben Vorrang vor dem globalen Standard, bis sie entfernt werden.",
                        empty: "Keine Überschreibungen pro Rechtseinheit konfiguriert. Alle Rechtseinheiten erben derzeit den globalen Standard.",
                    },
                    columns: {
                        overrideModel: "Überschriebenes Modell",
                    },
                    tooltips: {
                        adjustOverride: "Überschreibung anpassen",
                        removeOverride: "Überschreibung entfernen",
                    },
                    editDialog: {
                        title: "Überschreibung für Rechtseinheit anpassen",
                        matchesDefault:
                            "Dies entspricht dem globalen Standard. Entfernen Sie stattdessen die Überschreibung, wenn die Rechtseinheit ihn erben soll.",
                    },
                    removeDialog: {
                        title: "Überschreibung für Rechtseinheit entfernen",
                        message:
                            "Soll die Überschreibung für {{legalEntityName}} entfernt werden? Dann wird wieder das globale Standardmodell verwendet.",
                        confirm: "Überschreibung entfernen",
                        fallbackLegalEntity: "diese Rechtseinheit",
                    },
                },
                usage: {
                    eyebrow: "Abrechnungszeitraum",
                    title: "Nutzungsübersicht",
                    dailyMetric: "Tagesmetrik",
                    loadingBillingCycle: "Abrechnungszeitraum wird geladen",
                    selectLegalEntityToLoad: "Wählen Sie eine Rechtseinheit aus, um die Nutzung zu laden",
                    selectedLegalEntityFallback: "die ausgewählte Rechtseinheit",
                    selectionSummary:
                        "Anzeige für {{month}} {{year}} für {{legalEntityName}}. Der Zeitraum wird am 1. jedes Monats zurückgesetzt, und die Kosten sind hier weiterhin gemockt.",
                    dailyUsage: "Tägliche Nutzung",
                    previousWeek: "Vorherige Woche",
                    previousMonth: "Vorheriger Monat",
                    nextWeek: "Nächste Woche",
                    nextMonth: "Nächster Monat",
                    metricOnDate: "{{value}} am {{date}}",
                    loadingUsage: "Nutzung des Abrechnungszeitraums wird geladen...",
                    metrics: {
                        cost: "Kosten",
                        totalTokens: "Gesamttokens",
                        inputTokens: "Eingabetokens",
                        outputTokens: "Ausgabetokens",
                        messagesSent: "Gesendete Nachrichten",
                    },
                    emptyState: {
                        title: "Wählen Sie eine Rechtseinheit aus, um die Nutzung anzuzeigen.",
                        description:
                            "Abrechnungssummen und tägliche Aktivität werden angezeigt, sobald eine Rechtseinheit ausgewählt ist. Die täglichen Kosten sind in diesem Tab weiterhin gemockt.",
                    },
                    summary: {
                        costThisCycle: "Kosten in diesem Zyklus",
                        billingCycleStarted: "Abrechnungszeitraum begann am {{date}}",
                        aggregatedAcrossAllUsers: "Über alle Nutzer aggregiert",
                        filteredToSelectedUser: "Auf den ausgewählten Nutzer gefiltert",
                        totalTokens: "Gesamttokens",
                        allTokenUsage: "Gesamte Tokennutzung in diesem Zyklus",
                        selectedUserTokenUsage: "Tokennutzung für den ausgewählten Nutzer",
                    },
                },
                legalEntityManagement: {
                    eyebrow: "Arbeitsbereichsstruktur",
                    title: "Rechtseinheiten",
                    description:
                        "Erstellen und prüfen Sie Rechtseinheiten, die im gesamten Admin-Arbeitsbereich verwendet werden.",
                    actions: {
                        createLegalEntity: "Rechtseinheit erstellen",
                    },
                    columns: {
                        billingAddress: "Rechnungsadresse",
                        postalAddress: "Postadresse",
                        taxId: "Steuer-ID",
                    },
                    createDialog: {
                        title: "Neue Rechtseinheit erstellen",
                        optional: "Optional",
                    },
                },
                userManagement: {
                    eyebrow: "Arbeitsbereichszugriff",
                    title: "Nutzer",
                    selectLegalEntityAlert:
                        "Wählen Sie eine Rechtseinheit aus, um die Nutzertabelle für diesen Arbeitsbereich freizuschalten.",
                    emptyState: "Wählen Sie eine Rechtseinheit aus, um Ergebnisse der Nutzerverwaltung anzuzeigen.",
                    actions: {
                        createUser: "Nutzer erstellen",
                    },
                    columns: {
                        role: "Rolle",
                        preferredLocale: "Bevorzugte Sprache",
                    },
                    roles: {
                        admin: "Admin",
                        user: "Nutzer",
                    },
                    softDelete: {
                        tooltip: "Nutzer deaktivieren",
                        ariaLabel: "{{name}} deaktivieren",
                    },
                    createUserDialog: {
                        title: "Neuen Nutzer erstellen",
                        selectLegalEntity: "Rechtseinheit auswählen",
                    },
                },
                billing: {
                    eyebrow: "Rechnungsverwaltung",
                    title: "Rechnungsübersicht",
                    defaultPrice: {
                        eyebrow: "Standardpreis",
                        title: "Globalen Standardpreis konfigurieren",
                        description:
                            "Wird automatisch verwendet, wenn eine Rechtseinheit keine eigene Preisüberschreibung hat.",
                        label: "Standardpreis pro Token",
                        helperText:
                            "Geben Sie den Basispreis ein, der für alle Rechtseinheiten ohne Überschreibung gilt.",
                        save: "Standardpreis speichern",
                    },
                    legalEntityPrice: {
                        eyebrow: "Preis der Rechtseinheit",
                        title: "Preise für Rechtseinheit konfigurieren",
                        description:
                            "Setzen Sie einen eigenen Preis für die Rechtseinheit oder entfernen Sie die Überschreibung, damit wieder der Standardpreis gilt.",
                        label: "Preis pro Token für Rechtseinheit",
                        helperText: "Geben Sie den Preis pro Token für diese Rechtseinheit ein.",
                        overrideHelper:
                            "Diese Rechtseinheit verwendet derzeit eine eigene Preisüberschreibung.",
                        inheritsDefault:
                            "Diese Rechtseinheit verwendet derzeit den globalen Standardpreis.",
                        selectLegalEntity:
                            "Wählen Sie eine Rechtseinheit aus, um ihre Preisüberschreibung zu bearbeiten oder zu löschen.",
                        save: "Preis der Rechtseinheit speichern",
                        delete: "Überschreibung löschen",
                        deleteDialog: {
                            title: "Preis der Rechtseinheit löschen",
                            message:
                                "Soll die Preisüberschreibung für {{legalEntityName}} gelöscht werden? Dann gilt wieder der globale Standardpreis.",
                            confirm: "Überschreibung löschen",
                            fallbackLegalEntity: "diese Rechtseinheit",
                        },
                    },
                    timeGranularity: "Zeitgranularität",
                    chartTitle: "Abrechnungsaktivität",
                    chartRange: "{{startDate}} - {{endDate}}",
                    loading: "Abrechnungsdaten werden geladen...",
                    rangeIncomplete:
                        "Wählen Sie ein Start- und Enddatum aus, um Abrechnungsdaten zu laden.",
                    invalidRange: "Das Startdatum muss am oder vor dem Enddatum liegen.",
                    selectedLegalEntityFallback: "die ausgewählte Rechtseinheit",
                    selectionSummary:
                        "Anzeige der Abrechnung in {{granularity}} zwischen {{startDate}} und {{endDate}} für {{legalEntityName}}.",
                    metricOnDate: "{{value}} am {{date}}",
                    metrics: {
                        tokens: "Arena-Token",
                        price: "Preis",
                    },
                    granularity: {
                        daily: "Täglich",
                        weekly: "Wöchentlich",
                        monthly: "Monatlich",
                        yearly: "Jährlich",
                    },
                    emptyState: {
                        title: "Wählen Sie eine Rechtseinheit aus, um Abrechnungsdaten anzuzeigen.",
                        description:
                            "Abrechnungs-Buckets werden angezeigt, sobald eine Rechtseinheit ausgewählt ist und ein Datumsbereich vorliegt.",
                    },
                    noData: {
                        title: "Keine Abrechnungsdaten in diesem Zeitraum.",
                        description:
                            "Versuchen Sie einen anderen Datumsbereich oder eine gröbere Zeitgranularität.",
                    },
                    exportCsv: {
                        label: "CSV herunterladen",
                        loading: "CSV wird heruntergeladen...",
                    },
                    applyFilters: "Anwenden",
                },
            },
            settings: {
                title: "Einstellungen",
                language: "Sprache",
                theme: "Design",
                light: "Hell",
                dark: "Dunkel",
                en: "Englisch",
                de: "Deutsch",
                changePassword: "Passwort ändern",
                logout: "Abmelden",
                logoutConfirmation: "Möchten Sie sich wirklich abmelden?",
                openMenu: "Einstellungen öffnen",
                changePasswordDialog: {
                    title: "Passwort ändern",
                    currentPassword: "Aktuelles Passwort",
                    newPassword: "Neues Passwort",
                    confirmPassword: "Neues Passwort bestätigen",
                    submit: "Passwort ändern",
                    passwordsDoNotMatch: "Die neuen Passwörter stimmen nicht überein.",
                },
            },
            chat: {
                newChat: "Neuer Chat",
                copyToClipboard: "In die Zwischenablage kopieren",
                edit: "Nachricht bearbeiten",
                editPlaceholder: "Nachricht bearbeiten",
                saveEdit: "Bearbeitung speichern",
                cancelEdit: "Bearbeitung abbrechen",
                loading: "Lädt...",
                inputPlaceholder: 'Stellen sie ihre Frage zum Buch "Vertragsrecht im Einkauf"',
                send: "Senden",
                noChatSelected: "Wählen Sie einen bestehenden Chat oder starten Sie einen neuen, um zu beginnen.",
                attachments: {
                    openMenu: "Anhangsmenü öffnen",
                    uploadFile: "Datei hochladen",
                    dropPrompt: "PDF-, TXT-, DOC-, DOCX-, MD- oder PPTX-Dateien hier ablegen",
                    helperText: "Nur PDF, TXT, DOC, DOCX, MD oder PPTX, bis zu {{maxSizeMb}} MB pro Datei, maximal {{maxFiles}} Dateien.",
                    status: {
                        uploading: "Datei wird hochgeladen.",
                        unavailable: "Entfernen Sie abgelaufene oder fehlende Dateien vor dem Senden.",
                    },
                    tooltips: {
                        uploading: "Datei wird hochgeladen",
                        expired: "Diese Datei ist abgelaufen. Entfernen Sie sie und laden Sie sie erneut hoch.",
                        missing: "Diese Datei ist nicht mehr verfügbar. Entfernen Sie sie und laden Sie sie erneut hoch.",
                    },
                    errors: {
                        tooManyFiles: "Sie können maximal {{maxFiles}} Dateien hochladen.",
                        fileTooLarge: "{{fileName}} überschreitet das Limit von {{maxSizeMb}} MB.",
                        unsupportedType: "{{fileName}} ist kein unterstützter Dateityp. Laden Sie nur PDF-, TXT-, DOC-, DOCX-, MD- oder PPTX-Dateien hoch.",
                        duplicateFile: "{{fileName}} ist bereits angehängt und wurde ignoriert.",
                        duplicateFiles: "{{count}} doppelte Dateien wurden ignoriert.",
                    },
                },
            },
            chatHistory: {
                noHistory: "Kein Verlauf gefunden",
                moreActions: "Weitere Aktionen",
                rename: "Umbenennen",
                delete: "Löschen",
            },
            login: {
                signInTitle: "Anmelden",
                usernameLabel: "Benutzername",
                passwordLabel: "Passwort",
                signIn: "Anmelden",
                signingIn: "Anmelden...",
                fillAllFields: "Bitte füllen sie alle Felder aus.",
                loginFailed: "Anmeldung fehlgeschlagen. Bitte versuchen sie es erneut.",
            },
        },
    },
} as const;

//Before user logs in we use browser language
const browserLanguage = navigator.language.toLowerCase().startsWith("de") ? "de" : "en";

void i18n.use(initReactI18next).init({
    resources,
    lng: browserLanguage,
    fallbackLng: "en",
    interpolation: {
        escapeValue: false,
    },
});

export default i18n;
